import { useAuth } from "@/hooks/useAuth";
import { useSoloArtistSubscription } from "@/hooks/useSoloArtistSubscription";
import { Loader2, CreditCard } from "lucide-react";

export function SoloArtistRestriction({ children, restrictedFor = "solo" }: { children: React.ReactNode; restrictedFor?: "solo" | "shop" }) {
  const { memberships } = useAuth();
  const isSoloArtist = memberships.length === 0;

  if (restrictedFor === "solo" && isSoloArtist) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md p-8">
          <h1 className="font-display text-4xl text-foreground mb-4">Feature Not Available</h1>
          <p className="text-muted-foreground">
            This feature is only available for studio accounts.
            Upgrade to a Studio plan to access team management, apprenticeships, and more.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export function SoloProRestriction({ children, feature }: { children: React.ReactNode; feature: string }) {
  const { memberships } = useAuth();
  const isSoloArtist = memberships.length === 0;

  if (!isSoloArtist) {
    return <>{children}</>;
  }

  return <SoloProGate feature={feature}>{children}</SoloProGate>;
}

function SoloProGate({ children, feature }: { children: React.ReactNode; feature: string }) {
  const { tier, createCheckout } = useSoloArtistSubscription();

  if (tier === "pro" || tier === "shop_member") {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md text-center space-y-6 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
          <CreditCard className="w-8 h-8 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-3xl text-foreground mb-2">Pro Feature</h1>
          <p className="text-muted-foreground">
            {feature} is available on the Solo Artist Pro plan ($19/mo). Upgrade to unlock all features.
          </p>
        </div>
        <button
          onClick={() => createCheckout.mutate({ billingInterval: "monthly" })}
          disabled={createCheckout.isPending}
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-3 font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {createCheckout.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4" />
          )}
          Upgrade to Pro — $19/mo
        </button>
      </div>
    </div>
  );
}
